import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { NavBar } from '../appBars/navBar/navBar';
import { createParcelLockersClient, ServiceClient, SettingClient } from '../../helpers/client';
import { DeliveryType, ParcelLockerProvider, ServiceItem } from 'orderme-api-integration-client';
import { useTranslation } from 'react-i18next';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { clearDelivery, updateDeliveryService, updateFreeDeliveryFrom } from '../../redux/reducers/pickupReducer';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import { CartRoute, WhereToRoute } from '../../routes/allRoutes';
import { SalesPointItem } from './salesPointItem';
import { ParcelLockerItem } from './parcelLockerItem';
import { AddressItem } from './addressItem';
import { LockerItem } from './lockerItem';
import { RootState } from '../../redux/store';
import { BottomCartBar } from '../appBars/cartBar/bottomCartBar';
import { StickyCartBar } from '../appBars/cartBar/stickyCartBar';
import { updateDeliveryServiceAsync } from '../../helpers/deliveryHelper';
import { useRouteNavigator } from '../../routes/useRouteNavigator';

export interface DeliveryDetails {
  serviceId: string;
  freeDeliveryFrom: number;
  service: ServiceItem | undefined;
  price: number;
}

export function WhereTo() {
  const dispatch = useAppDispatch();
  const routeNavigator = useRouteNavigator();
  const isUserLoggedIn = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const onlyPhysicalItems = useAppSelector((state: RootState) => state.shoppingCartState.onlyPhysicalItems);
  const deliveryType = useAppSelector((state: RootState) => state.pickupState.deliveryType);
  const settingClient: SettingClient = useMemo(() => new SettingClient(), []);
  const serviceClient: ServiceClient = useMemo(() => new ServiceClient(), []);
  const parcelLockersClient = useMemo(() => createParcelLockersClient(), []);
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryType>();
  const [parcelLockerProviders, setParcelLockerProviders] = useState<ParcelLockerProvider[]>();
  const [canConfirmDelivery, setCanConfirmDelivery] = useState<boolean>(false);

  const initialDeliveryState: DeliveryDetails = {
    serviceId: '',
    freeDeliveryFrom: 0,
    service: undefined,
    price: 0,
  };

  const [homeDelivery, setHomeDelivery] = useState<DeliveryDetails>(initialDeliveryState);
  const [parcelLockerDelivery, setParcelLockerDelivery] = useState<DeliveryDetails>(initialDeliveryState);
  const [businessLockerDelivery, setBusinessLockerDelivery] = useState<DeliveryDetails>(initialDeliveryState);

  const { t } = useTranslation();

  const getDeliverySettings = useCallback(() => {
    settingClient
      .get()
      .then((response) => {
        setHomeDelivery((prev) => ({
          ...prev,
          serviceId: response.orders.deliveryServiceId,
          freeDeliveryFrom: response.orders.freeDeliverFrom,
        }));
        setParcelLockerDelivery((prev) => ({
          ...prev,
          serviceId: response.orders.parcelLockerDeliveryServiceId,
          freeDeliveryFrom: response.orders.parcelLockerFreeDeliverFrom,
        }));
        setBusinessLockerDelivery((prev) => ({
          ...prev,
          serviceId: response.orders.wardrobeDeliveryServiceId,
          freeDeliveryFrom: response.orders.wardrobeFreeDeliverFrom,
        }));
      })
      .catch((err) => console.log(err));
  }, [settingClient]);

  useEffect(() => {
    getDeliverySettings();
    parcelLockersClient
      .getProviders()
      .then((response) => {
        if (response) {
          setParcelLockerProviders(response);
        } else {
          setParcelLockerProviders([]);
        }
      })
      .catch((err) => {
        console.error(err);
        setParcelLockerProviders([]);
      });
    dispatch(clearDelivery());
  }, [dispatch, getDeliverySettings, parcelLockersClient]);

  useEffect(() => {
    if (!homeDelivery.serviceId) {
      return;
    }

    serviceClient
      .getServiceById(homeDelivery.serviceId)
      .then((response) => {
        setHomeDelivery((prev) => ({
          ...prev,
          service: response,
          price: response.price,
        }));
      })
      .catch((err) => console.log(err));
  }, [homeDelivery.serviceId, serviceClient]);

  useEffect(() => {
    setCanConfirmDelivery(deliveryType === DeliveryType.SalesPoint || (isUserLoggedIn && !!deliveryType));
  }, [deliveryType, isUserLoggedIn]);

  useEffect(() => {
    if (!isUserLoggedIn && selectedDelivery !== DeliveryType.SalesPoint) {
      setSelectedDelivery(DeliveryType.SalesPoint);
    }
  }, [isUserLoggedIn, selectedDelivery]);

  useEffect(() => {
    if (!parcelLockerDelivery.serviceId) {
      return;
    }

    serviceClient
      .getServiceById(parcelLockerDelivery.serviceId)
      .then((response) => {
        setParcelLockerDelivery((prev) => ({
          ...prev,
          service: response,
          price: response.price,
        }));
      })
      .catch((err) => console.log(err));
  }, [parcelLockerDelivery.serviceId, serviceClient]);

  useEffect(() => {
    if (!businessLockerDelivery.serviceId) {
      return;
    }

    serviceClient
      .getServiceById(businessLockerDelivery.serviceId)
      .then((response) => {
        setBusinessLockerDelivery((prev) => ({
          ...prev,
          service: response,
          price: response.price,
        }));
      })
      .catch((err) => console.log(err));
  }, [businessLockerDelivery.serviceId, serviceClient]);

  const openPage = (type: DeliveryType) => {
    if (!isUserLoggedIn && type !== DeliveryType.SalesPoint) {
      return;
    }

    setSelectedDelivery(type);
    switch (type) {
      case DeliveryType.Wardrobe:
        dispatch(updateDeliveryService(businessLockerDelivery.service));
        dispatch(updateFreeDeliveryFrom(businessLockerDelivery.freeDeliveryFrom));
        break;
      case DeliveryType.Address:
        dispatch(updateDeliveryService(homeDelivery.service));
        dispatch(updateFreeDeliveryFrom(homeDelivery.freeDeliveryFrom));
        break;
      case DeliveryType.ForeignLocker:
        dispatch(updateDeliveryService(parcelLockerDelivery.service));
        dispatch(updateFreeDeliveryFrom(parcelLockerDelivery.freeDeliveryFrom));
        break;
      default:
        dispatch(updateDeliveryService(null));
        dispatch(updateFreeDeliveryFrom(null));
        break;
    }
  };

  const confirmDelivery = async () => {
    try {
      switch (deliveryType) {
        case DeliveryType.Wardrobe:
          await updateDeliveryServiceAsync(businessLockerDelivery.serviceId);
          break;
        case DeliveryType.Address:
          await updateDeliveryServiceAsync(homeDelivery.serviceId);
          break;
        case DeliveryType.ForeignLocker:
          await updateDeliveryServiceAsync(parcelLockerDelivery.serviceId);
          break;
        default:
          await updateDeliveryServiceAsync(null);
          break;
      }
      routeNavigator.goToOrderCheckout();
    } catch (ex) {
      console.error('Not possible to update delivery service.', ex);
      return;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NavBar barTitle={t('payment:whereToDeliver')} />
      <BreadcrumbsBar paths={[{ path: CartRoute, isLink: true }, { path: WhereToRoute }]} />
      <Box
        sx={{
          backgroundColor: 'white',
          color: 'black',
          borderRadius: '15px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ width: appStyleMode === AppStyle.Mobile ? '100%' : '95%' }}>
          <Box className={appStyleMode === AppStyle.Mobile && 'where-to-list'}>
            {onlyPhysicalItems ? (
              <Typography sx={{ fontSize: '24px', my: '20px' }}>
                {t('payment:whereToPickupPart1')}
                <Typography component="span" color="secondary" sx={{ fontSize: '24px' }}>
                  {t('payment:whereToPickupPart2Colored')}
                </Typography>
                {t('payment:whereToPickupPart3')}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: '24px', my: '20px' }}>
                {t('payment:whereToDeliverPart1')}
                <Typography component="span" color="secondary" sx={{ fontSize: '24px' }}>
                  {t('payment:whereToDeliverPart2Colored')}
                </Typography>
                {t('payment:whereToDeliverPart3')}
              </Typography>
            )}
            <SalesPointItem
              isSelected={selectedDelivery === DeliveryType.SalesPoint}
              itemMustBeSelected={onlyPhysicalItems}
              onClick={() => openPage(DeliveryType.SalesPoint)}
            />

            {isUserLoggedIn &&
              parcelLockerProviders?.map((option) => (
                <ParcelLockerItem
                  deliveryDetails={parcelLockerDelivery}
                  provider={option}
                  isSelected={selectedDelivery === DeliveryType.ForeignLocker}
                  onClick={() => openPage(DeliveryType.ForeignLocker)}
                />
              ))}
            {isUserLoggedIn && (
              <AddressItem
                deliveryDetails={homeDelivery}
                isSelected={selectedDelivery === DeliveryType.Address}
                onClick={() => openPage(DeliveryType.Address)}
              />
            )}
            {isUserLoggedIn && !onlyPhysicalItems && (
              <LockerItem
                deliveryDetails={businessLockerDelivery}
                isSelected={selectedDelivery === DeliveryType.Wardrobe}
                onClick={() => openPage(DeliveryType.Wardrobe)}
              />
            )}
            {appStyleMode === AppStyle.Mobile ? (
              <BottomCartBar barText={t('payment:continue')} open={confirmDelivery} isDisabled={!canConfirmDelivery} />
            ) : (
              <StickyCartBar
                sx={{ mb: '15px' }}
                barText={t('payment:continue')}
                open={confirmDelivery}
                isDisabled={!canConfirmDelivery}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
